// Legal Documents for Dr. Carlos Castellon Medical Practice
// These are templates - recommend legal review before use

export const privacyPolicyFull = {
  title: "Privacy Policy",
  lastUpdated: "January 2025",
  content: `
PRIVACY POLICY

Dr. Carlos Castellon, M.D.
General & Preventative Medicine
Ibiza, Spain

Last Updated: January 2025

1. INTRODUCTION

This Privacy Policy explains how Dr. Carlos Castellon, M.D. ("we," "us," or "our") collects, uses, stores, and protects your personal information in accordance with the European Union General Data Protection Regulation (GDPR), Spanish Organic Law 3/2018 on Personal Data Protection (LOPD-GDD), and applicable Spanish healthcare regulations.

2. DATA CONTROLLER

Dr. Carlos Castellon, M.D.
Medical License No.: 072870415
Ibiza, Balearic Islands, Spain

3. PERSONAL DATA WE COLLECT

We collect the following categories of personal data:

a) Identity Data: Full name, date of birth, passport/ID number, nationality
b) Contact Data: Email address, telephone number, physical address
c) Health Data: Medical history, symptoms, diagnoses, treatment plans, prescriptions, laboratory results
d) Technical Data: IP address, browser type, device information (for telemedicine services)
e) Financial Data: Payment information, billing records

4. HOW WE COLLECT YOUR DATA

We collect personal data through:
- Direct interactions (appointments, consultations, intake forms)
- Telemedicine platforms
- Email and telephone communications
- Third-party platforms used for communication

5. LEGAL BASIS FOR PROCESSING

We process your personal data based on:
- Your explicit consent (Article 6(1)(a) and Article 9(2)(a) GDPR)
- Performance of a medical contract (Article 6(1)(b) GDPR)
- Legal obligations (Article 6(1)(c) GDPR)
- Vital interests (Article 6(1)(d) GDPR) in emergencies
- Legitimate interests (Article 6(1)(f) GDPR)

For health data specifically, we rely on:
- Explicit consent for medical treatment
- Provision of healthcare (Article 9(2)(h) GDPR)

6. PURPOSE OF DATA PROCESSING

Your personal data is processed for:
- Providing medical consultations and treatment
- Managing appointments and scheduling
- Maintaining medical records as required by Spanish law
- Processing payments and billing
- Communicating about your healthcare
- Complying with legal and regulatory obligations
- Improving our medical services

7. DATA RETENTION

Medical records are retained for a minimum of 5 years following the last treatment, or longer as required by Spanish healthcare regulations (Law 41/2002). Financial records are retained for 6 years in accordance with Spanish tax law.

8. DATA SHARING

We may share your data with:
- Laboratories and diagnostic centers (with your consent)
- Other healthcare providers for referrals (with your consent)
- Spanish health authorities (when legally required)
- Payment processors (for billing purposes)
- Professional advisors (lawyers, accountants) under confidentiality

We do NOT sell your personal data to third parties.

9. INTERNATIONAL TRANSFERS

If data is transferred outside the EEA, we ensure appropriate safeguards through Standard Contractual Clauses or adequacy decisions.

10. YOUR RIGHTS

Under GDPR, you have the right to:
- Access your personal data
- Rectify inaccurate data
- Erase your data ("right to be forgotten") where legally permissible
- Restrict processing
- Data portability
- Object to processing
- Withdraw consent at any time
- Lodge a complaint with the Spanish Data Protection Agency (AEPD)

To exercise these rights, contact us in writing.

11. DATA SECURITY

We implement appropriate technical and organizational measures including:
- Encrypted communications
- Secure storage systems
- Access controls and authentication
- Regular security assessments
- Staff confidentiality agreements

12. COOKIES AND TRACKING

Our website may use essential cookies for functionality. No marketing or tracking cookies are used without your explicit consent.

13. CHANGES TO THIS POLICY

We may update this Privacy Policy periodically. Changes will be posted on our website with the updated date.

14. CONTACT

For privacy-related inquiries or to exercise your rights:
Dr. Carlos Castellon, M.D.
Ibiza, Spain

15. SUPERVISORY AUTHORITY

You may lodge a complaint with:
Agencia Española de Protección de Datos (AEPD)
www.aepd.es
`
};

export const healthDataConsent = {
  title: "Health Data Consent",
  lastUpdated: "January 2025",
  content: `
HEALTH DATA CONSENT FORM

Dr. Carlos Castellon, M.D.
General & Preventative Medicine

CONSENT FOR COLLECTION AND PROCESSING OF HEALTH DATA

I, the undersigned patient, hereby provide my explicit consent for Dr. Carlos Castellon, M.D. to collect, process, and store my personal health information as described below:

1. TYPES OF HEALTH DATA COLLECTED

- Personal identification (name, date of birth, ID/passport number)
- Contact information (phone, email, address)
- Medical history and current health conditions
- Symptoms, diagnoses, and clinical observations
- Treatment plans and prescriptions
- Laboratory and diagnostic test results
- Allergies and medication history
- Lifestyle and relevant social history

2. PURPOSE OF COLLECTION

My health data will be used for:
- Providing medical diagnosis and treatment
- Maintaining continuity of care
- Prescribing medications when appropriate
- Referring to specialists when necessary
- Complying with Spanish medical record-keeping requirements
- Billing and administrative purposes

3. DATA STORAGE AND SECURITY

I understand that:
- My health data will be stored securely in compliance with GDPR and Spanish law
- Access is limited to Dr. Castellon and authorized medical staff
- Data is encrypted and protected against unauthorized access
- Medical records are retained for a minimum of 5 years as required by Law 41/2002

4. DATA SHARING

My health data may be shared with:
- Laboratories for diagnostic testing (with my knowledge)
- Other healthcare providers for referrals (with my explicit consent)
- Spanish health authorities when legally required
- Emergency services in life-threatening situations

My health data will NOT be:
- Sold to third parties
- Used for marketing purposes
- Shared without my consent except as legally required

5. MY RIGHTS

I have the right to:
- Access my health records
- Request corrections to inaccurate information
- Request deletion of my data (where legally permissible)
- Withdraw this consent at any time
- Receive a copy of my medical records
- Lodge a complaint with the AEPD

6. WITHDRAWAL OF CONSENT

I understand that I may withdraw this consent at any time by written notice. Withdrawal does not affect the lawfulness of processing before withdrawal. Certain data may be retained as required by law.

7. DECLARATION

By providing this consent, I confirm that:
- I have read and understood this document
- I voluntarily consent to the collection and processing of my health data
- I have had the opportunity to ask questions
- I understand my rights regarding my personal data

This consent is valid from the date of signature until withdrawn in writing.
`
};

export const telemedicineConsentFull = {
  title: "Telemedicine Informed Consent",
  lastUpdated: "January 2025",
  content: `
TELEMEDICINE INFORMED CONSENT

Dr. Carlos Castellon, M.D.
General & Preventative Medicine
Ibiza, Spain

INFORMED CONSENT FOR TELEMEDICINE SERVICES

1. DEFINITION OF TELEMEDICINE

Telemedicine involves the delivery of healthcare services using electronic communications, including video consultations, telephone consultations, and secure messaging, when the patient and physician are not in the same physical location.

2. BENEFITS OF TELEMEDICINE

- Convenient access to medical care from any location
- Reduced travel time and associated costs
- Timely medical consultation
- Continuity of care for follow-up appointments
- Access to English-speaking medical care

3. LIMITATIONS AND RISKS

I understand and acknowledge that:

a) Technical Limitations:
- Technology failures may interrupt or prevent the consultation
- Audio/video quality may affect the consultation
- Internet connectivity issues are beyond the physician's control

b) Clinical Limitations:
- Physical examination is not possible remotely
- Certain conditions require in-person evaluation
- The physician may determine that telemedicine is not appropriate for my condition
- Diagnostic accuracy may be limited without physical examination

c) Privacy Risks:
- Electronic communications carry inherent security risks
- Despite encryption and security measures, absolute privacy cannot be guaranteed
- I am responsible for ensuring privacy on my end of the communication

4. WHAT TELEMEDICINE IS NOT

Telemedicine services are NOT appropriate for:
- Medical emergencies
- Life-threatening conditions
- Conditions requiring immediate physical intervention
- Situations requiring hands-on examination

IN CASE OF EMERGENCY: Contact local emergency services (112 in Spain) immediately.

5. MY RESPONSIBILITIES

I agree to:
- Provide accurate and complete health information
- Ensure a private location for the consultation
- Have valid identification available
- Test my technology before the appointment
- Inform the physician if I am located outside of Spain
- Follow up in person if recommended by the physician

6. PRESCRIPTIONS

I understand that:
- Prescriptions are issued at the physician's discretion
- Not all medications can be prescribed via telemedicine
- Controlled substances, narcotics, and sedatives are NOT prescribed via telemedicine
- Prescriptions comply with Spanish medical regulations
- A physical examination may be required before certain prescriptions

7. FEES AND PAYMENT

- Telemedicine consultations are billable services
- Payment is required at the time of booking
- Cancellation policies apply as stated in Terms of Service
- Technical failures initiated on my end do not constitute grounds for refund

8. MEDICAL RECORDS

Telemedicine consultations are documented in my medical record in accordance with Spanish law, with the same confidentiality as in-person visits.

9. CONSENT TO TREATMENT

By proceeding with a telemedicine consultation, I consent to:
- Receiving medical consultation via electronic means
- The physician making clinical decisions based on available information
- Treatment recommendations within the scope of telemedicine

10. RIGHT TO WITHDRAW

I may withdraw this consent at any time and request an in-person consultation instead.

11. DECLARATION

By accepting this consent, I confirm that:
- I have read and understood this document
- I understand the benefits, limitations, and risks of telemedicine
- I have had the opportunity to ask questions
- I voluntarily consent to receive telemedicine services
- I understand this does not guarantee any specific treatment or prescription
`
};

export const termsOfService = {
  title: "Terms of Service",
  lastUpdated: "January 2025",
  content: `
TERMS OF SERVICE

Dr. Carlos Castellon, M.D.
General & Preventative Medicine
Ibiza, Spain

Last Updated: January 2025

Please read these Terms of Service carefully before using our medical services.

1. ACCEPTANCE OF TERMS

By booking an appointment, using our services, or accessing our website, you agree to be bound by these Terms of Service, our Privacy Policy, Health Data Consent, and Telemedicine Consent (where applicable).

2. MEDICAL SERVICES

2.1 Scope of Services
Dr. Carlos Castellon, M.D. provides:
- Private medical consultations (home visits)
- Telemedicine consultations
- Prescription management (all refills conducted as clinical consultations and billed at €150)
- General and preventative medicine
- Pediatric care

2.2 Limitations
Our services do NOT include:
- Emergency medical care
- IV vitamin or infusion therapies
- Controlled substance prescriptions via telemedicine
- Services outside the scope of general medicine

2.3 Medical Decisions
All medical advice, diagnoses, treatment plans, and prescriptions are provided at the sole discretion of the physician based on clinical judgment and in accordance with Spanish medical law.

2.4 Right of Refusal
Dr. Carlos Castellon, M.D. reserves the right to:
- Decline to accept any individual as a patient
- Decline to provide specific treatments or prescriptions
- Refuse to prescribe controlled substances, narcotics, sedatives, or any medication deemed clinically inappropriate
- Discontinue the physician-patient relationship with reasonable notice
- Refuse service to individuals who are abusive, threatening, or non-compliant with medical advice

This right is exercised at the physician's sole discretion and in accordance with Spanish medical ethics and regulations. Refusal to treat does not constitute discrimination and may be based on clinical judgment, safety concerns, scope of practice limitations, or professional boundaries.

2.5 No Guarantee of Treatment
Submission of a booking request or inquiry does not guarantee acceptance as a patient or provision of any specific treatment, medication, or medical certificate. All medical decisions are made following proper clinical evaluation.

3. APPOINTMENTS AND SCHEDULING

3.1 Booking
Appointments are arranged by direct contact via email or WhatsApp.

3.2 Cancellation Policy
- Cancellations made more than 24 hours before the appointment: Full refund
- Cancellations made within 24 hours: 50% charge applies
- No-shows without notice: Full charge applies

3.3 Late Arrivals
- Patients arriving more than 15 minutes late may need to reschedule
- The full consultation fee may still apply

3.4 Physician Cancellation
If the physician must cancel, you will receive a full refund or the option to reschedule.

4. FEES AND PAYMENT

4.1 Payment Terms
- Payment is required at the time of booking for telemedicine consultations
- Home visits may be paid at the time of service
- We accept: Credit/debit cards, bank transfer, PayPal

4.2 Pricing
Prices are as listed on our booking platform. Prices may change without notice but confirmed bookings honor the quoted price.

4.3 Insurance
We do not accept insurance. Services are private-pay only. We can provide documentation for potential reimbursement from your insurer.

5. PATIENT RESPONSIBILITIES

You agree to:
- Provide accurate personal and health information
- Disclose all relevant medical history, medications, and allergies
- Follow medical advice and treatment plans
- Attend scheduled appointments or cancel in advance
- Pay for services as agreed
- Treat staff with respect

6. PHYSICIAN RESPONSIBILITIES

Dr. Castellon agrees to:
- Provide care in accordance with Spanish medical standards
- Maintain patient confidentiality
- Keep accurate medical records
- Refer to specialists when appropriate
- Respond to patient communications in a timely manner

7. NOT AN EMERGENCY SERVICE

Our practice is NOT an emergency medical service. 

FOR EMERGENCIES: Call 112 (Spain) or go to the nearest hospital emergency department immediately.

8. LIMITATION OF LIABILITY

8.1 No Guarantees
Medicine is not an exact science. No guarantees are made regarding:
- Specific medical outcomes or results
- Availability of appointments
- Provision of specific treatments or prescriptions
- Medical certificates or documentation

Individual responses to treatment vary and outcomes cannot be predicted with certainty.

8.2 Maximum Liability
To the fullest extent permitted by Spanish law, our liability is limited to the fees paid for the specific service giving rise to the claim.

8.3 Exclusions
We are not liable for:
- Damages arising from patient non-compliance with medical advice
- Outcomes of referrals to third-party providers
- Technical failures during telemedicine consultations
- Circumstances beyond our reasonable control
- Decisions made by the patient against medical advice
- Delays in seeking emergency care

8.4 Indemnification
You agree to indemnify and hold harmless Dr. Carlos Castellon, M.D. from any claims, damages, or expenses arising from your misuse of services, provision of false information, or violation of these Terms.

9. INTELLECTUAL PROPERTY

All content on our website, including text, images, and logos, is our property and may not be reproduced without permission.

10. PRIVACY

Your personal and health data is handled in accordance with our Privacy Policy, GDPR, and Spanish data protection law.

11. GOVERNING LAW

These Terms are governed by the laws of Spain. Any disputes shall be resolved in the courts of Ibiza, Balearic Islands, Spain.

12. CHANGES TO TERMS

We may modify these Terms at any time. Continued use of our services constitutes acceptance of modified Terms.

13. SEVERABILITY

If any provision of these Terms is found invalid, the remaining provisions continue in effect.

14. CONTACT

For questions about these Terms:
Dr. Carlos Castellon, M.D.
Ibiza, Spain

15. ACKNOWLEDGMENT

By booking an appointment or using our services, you acknowledge that you have read, understood, and agree to these Terms of Service.
`
};
